import { Box, Button, Step, StepLabel, Stepper, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import CompanyInformation from './ClientForm/CompanyInformation'
import OwnerInformation from './ClientForm/OwnerInformation'
import { FormikValues, useFormik } from 'formik'
import * as Yup from "yup";
import ReviewForm from './ClientForm/ReviewForm'
import { toast } from 'react-toastify'
import { ApiCreateClient, ApiGetClientList, ApiUpdateClient } from '../../../Core/Apicall'
import { useNavigate, useParams } from 'react-router-dom'
import { Client } from '../../../types/Client'



export const companySchema = Yup.object({
  // companyLogo: Yup.mixed().required("Company logo is required"),

  companyName: Yup.string()
    .trim()
    .required("Company name is required"),

  businessType: Yup.string()
    .required("Business type is required"),

  companyEmail: Yup.string()
    .email("Enter a valid email")
    .required("Company email is required"),

  companyPhone: Yup.string()
    .matches(/^[0-9]{10}$/, "Enter a valid 10-digit phone number")
    .required("Company phone is required"),

  // website: Yup.string()
  //   .url("Enter a valid website URL")
  //   .required("Website is required"),

  gstNumber: Yup.string()
    .matches(
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      "Enter a valid GST number"
    )
    .required("GST number is required"),

  panNumber: Yup.string()
    .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Enter a valid PAN number")
    .required("PAN number is required"),

  registrationNumber: Yup.string()
    .trim()
    .required("Registration number is required"),

  // Address
  addressLine1: Yup.string()
    .trim()
    .required("Address is required"),

  city: Yup.string()
    .trim()
    .required("City is required"),

  state: Yup.string()
    .trim()
    .required("State is required"),

  country: Yup.string()
    .trim()
    .required("Country is required"),

  postalCode: Yup.string()
    .matches(/^[0-9]{6}$/, "Enter a valid postal code")
    .required("Postal code is required"),
})

export const getOwnerSchema = (isEditMode: boolean) => Yup.object({
  // Owner
  firstName: Yup.string()
    .trim()
    .required("First name is required"),

  lastName: Yup.string()
    .trim()
    .required("Last name is required"),

  designation: Yup.string()
    .trim()
    .required("Designation is required"),

  email: Yup.string()
    .email("Enter a valid email")
    .required("Email is required"),

  mobileNumber: Yup.string()
    .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
    .required("Mobile number is required"),

  // alternateMobileNumber: Yup.string()
  //   .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
  //   .required("Alternate mobile number is required"),

  password: isEditMode
    ? Yup.string()
      .notRequired()
      .test(
        "password-min-length",
        "Password must be at least 8 characters",
        (value) => !value || value.length >= 8
      )
    : Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),

  confirmPassword: isEditMode
    ? Yup.string().when("password", {
      is: (password: string) => Boolean(password),
      then: (schema) =>
        schema
          .required("Confirm password is required")
          .oneOf(
            [Yup.ref("password")],
            "Passwords must match"
          ),
      otherwise: (schema) => schema.notRequired(),
    })
    : Yup.string()
      .required("Confirm password is required")
      .oneOf(
        [Yup.ref("password")],
        "Passwords must match"
      ),
})


function AddNewClient() {
  const [activeStep, setActiveStep] = useState<number>(0)

  const steps = ['Company Information', "Owner Information", "Review & Confirm"]
  const navigate = useNavigate()
  const { clientId } = useParams();
  const [clientDetails, setClientDetails] = useState<Client | null>()
  const isEditMode = !!clientId;
  const ownerSchema = getOwnerSchema(isEditMode);
  const fullSchema = companySchema.concat(ownerSchema);


  const getClientDetails = async () => {
    setClientDetails(null)
    try {
      const data = `?clientId=${clientId}`
      const res = await ApiGetClientList(data)
      if (res?.status) {
        setClientDetails(res?.data[0])
      }

    } catch (error: any) {
      toast.error(error?.message || "Something went wrong")
    }
  }

  useEffect(() => {
    clientId && getClientDetails()
  }, [clientId])


  const formik = useFormik({
    initialValues: {
      // Company
      companyLogo: null,
      companyName: clientDetails?.companyName || "",
      businessType: clientDetails?.businessType || null,
      companyEmail: clientDetails?.email || "",
      companyPhone: clientDetails?.mobile || "",
      website: clientDetails?.website || "",
      gstNumber: clientDetails?.gstNumber || "",
      panNumber: clientDetails?.panNumber || "",
      registrationNumber: clientDetails?.registrationNumber || "",

      // Address
      addressLine1: clientDetails?.address || "",
      city: clientDetails?.city || "",
      state: clientDetails?.state || "",
      country: clientDetails?.country || "",
      postalCode: clientDetails?.postalCode || "",

      // Owner
      firstName: clientDetails?.clientAdmin?.firstName || "",
      lastName: clientDetails?.clientAdmin?.lastName || "",
      designation: clientDetails?.clientAdmin?.designation || "",
      email: clientDetails?.clientAdmin?.email || "",
      mobileNumber: clientDetails?.clientAdmin?.mobile || "",
      // alternateMobileNumber: "",

      // Login
      password: "",
      confirmPassword: "",
    },
    validationSchema: fullSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const params = `?clientId=${clientId}`
        const data = {
          "companyName": values.companyName,
          "contactPerson": `${values.firstName} ${values.lastName}`,
          "email": values.companyEmail,
          "mobile": values.companyPhone,
          "gstNumber": values.gstNumber,
          "panNumber": values.panNumber,
          "website": values.website,
          "address": values.addressLine1,
          "city": values.city,
          "state": values.state,
          "country": values.country,
          "postalCode": values.postalCode,
          "businessType": values.businessType,
          "registrationNumber": values.registrationNumber,
          "owner": {
            "firstName": values.firstName,
            "lastName": values.lastName,
            "email": values.email,
            "mobile": values.mobileNumber,
            "designation": values.designation,
          }
        }

        const res = isEditMode ? await ApiUpdateClient(params, data) : await ApiCreateClient(data)
        if (res?.status) {
          navigate(-1)
        }
      } catch (error: any) {
        toast.error(error?.message || "Something went wrong")
      }
      console.log(values);
    },
  });
  console.log("Values", formik.values)

  const validationSchemas = [
    companySchema,
    ownerSchema,
    null,
  ];
  const handleNext = async () => {
    const schema = validationSchemas[activeStep];
    if (!schema) {
      formik.handleSubmit();
      return;
    }

    try {
      await schema.validate(formik.values, {
        abortEarly: false,
      });

      setActiveStep((prev) => prev + 1);
    } catch (err: any) {
      const errors: Record<string, string> = {};
      const touched: Record<string, boolean> = {};

      err.inner.forEach((error: any) => {
        console.log(`${error.path}: ${error.message}`);

        errors[error.path] = error.message;
        touched[error.path] = true;
      });


      formik.setErrors(errors);
      formik.setTouched(touched);

      return;
    }
  };
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const RenderStep = () => {
    switch (activeStep) {
      case 0:
        return <CompanyInformation formik={formik} />;

      case 1:
        return <OwnerInformation formik={formik} />;

      case 2:
        return <ReviewForm formik={formik} />;

      default:
        return <CompanyInformation formik={formik} />;
    }
  };
  return (
    <Box className='p-lg-3 p-xl-4 p-2 stepper'>
      <Typography variant='h5' className='fw-bold'>Add New Client</Typography>

      <Stepper
        className='gap-5 mx-auto p-0 w-100 fs-20 position-relative'
        activeStep={activeStep}
        alternativeLabel
        sx={{
          width: "fit-content",
          "& .MuiStepLabel-label.Mui-active": {
            color: "var(--primary-color) !important",
            fontWeight: 600,
          },

          // Completed label
          "& .MuiStepLabel-label.Mui-completed": {
            color: "var( --primary-dark-color) !important",
            fontWeight: 600,
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel >{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {RenderStep()}

      <Box className='d-flex align-items-center justify-content-between mt-4'>
        <Button
          variant="outlined"
          className='btn-outlined-main'
          onClick={handleBack}
          disabled={activeStep === 0}
        >
          Back
        </Button>

        <Button
          variant="contained"
          className='btn-main'
          onClick={handleNext}
          sx={{
            backgroundColor: "var(--primary-color)",
            "&:hover": {
              backgroundColor: "var(--primary-dark-color)",
            },
          }}
        >
          {activeStep === steps.length - 1 ? `${isEditMode ? "Update" : "Finish"}` : "Next"}
        </Button>
      </Box>
    </Box>
  )
}

export default AddNewClient
